import { Routes, Route } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Header } from './components/layout/Header';
import { Footer } from './components/layout/Footer';
import { ScrollToTop } from './components/utils/ScrollToTop';
import { HomePage } from './components/pages/HomePage';
import { ServicesPage } from './components/pages/ServicesPage';
import { ProductsPage } from './components/pages/ProductsPage';
import { BlogPage } from './components/pages/BlogPage';
import { BlogDetailPage } from './components/pages/BlogDetailPage';
import { ContactPage } from './components/pages/ContactPage';
import { AboutPage } from './components/pages/AboutPage';
import { CareersPage } from './components/pages/CareersPage';
import { TermsPage } from './components/pages/TermsPage';
import { PrivacyPage } from './components/pages/PrivacyPage';
import { SecurityPage } from './components/pages/SecurityPage';
import { CookiesPage } from './components/pages/CookiesPage';
import { DashboardPage } from './components/pages/DashboardPage';
import { AuthProvider } from './context/AuthContext';
import { AuthModalProvider, useAuthModal } from './context/AuthModalContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { AuthModal } from './components/auth/AuthModal';
import { CookieConsent } from './components/ui/CookieConsent';

// Admin Components
import { AdminLayout } from './components/admin/AdminLayout';
import { AdminLogin } from './components/admin/AdminLogin';
import { AdminDashboard } from './components/admin/AdminDashboard';
import { AdminAnalytics } from './components/admin/AdminAnalytics';
import { AdminBlog } from './components/admin/AdminBlog';
import { AdminProjects } from './components/admin/AdminProjects';
import { AdminMessages } from './components/admin/AdminMessages';
import { AdminContent } from './components/admin/AdminContent';
import { AdminTranslations } from './components/admin/AdminTranslations';
import { AdminMedia } from './components/admin/AdminMedia';
import { AdminSEO } from './components/admin/AdminSEO';
import { AdminSecurity } from './components/admin/AdminSecurity';
import { AdminEmail } from './components/admin/AdminEmail';
import { AdminActivity } from './components/admin/AdminActivity';
import { AdminSettings } from './components/admin/AdminSettings';
import { AdminUsers } from './components/admin/AdminUsers';
import { AdminAIManagement } from './components/admin/AdminAIManagement';
import { AdminTenants } from './components/admin/AdminTenants';

// Global Auth Modal component
function GlobalAuthModal() {
  const { isOpen, closeAuthModal } = useAuthModal();
  return <AuthModal isOpen={isOpen} onClose={closeAuthModal} />;
}

function App() {
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');

  useEffect(() => {
    // Check system preference
    const mediaQuery = window.matchMedia('(prefers-color-scheme: light)');
    const savedTheme = localStorage.getItem('ochtopus-theme') as 'dark' | 'light' | null;

    if (savedTheme) {
      setTheme(savedTheme);
    } else if (mediaQuery.matches) {
      setTheme('light');
    }
  }, []);

  useEffect(() => {
    document.documentElement.classList.remove('dark', 'light');
    document.documentElement.classList.add(theme);
    localStorage.setItem('ochtopus-theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <AuthProvider>
      <SubscriptionProvider>
        <AuthModalProvider>
          <GlobalAuthModal />
          <CookieConsent />
          <Routes>
          {/* Admin Routes - No Header/Footer */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="blog" element={<AdminBlog />} />
            <Route path="projects" element={<AdminProjects />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="translations" element={<AdminTranslations />} />
            <Route path="media" element={<AdminMedia />} />
            <Route path="seo" element={<AdminSEO />} />
            <Route path="security" element={<AdminSecurity />} />
            <Route path="email" element={<AdminEmail />} />
            <Route path="activity" element={<AdminActivity />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="ai-management" element={<AdminAIManagement />} />
            <Route path="tenants" element={<AdminTenants />} />
          </Route>

          {/* Public Routes - With Header/Footer */}
          <Route
            path="/*"
            element={
              <div className="min-h-screen flex flex-col">
                <ScrollToTop />
                <Header theme={theme} toggleTheme={toggleTheme} />
                <main className="flex-1">
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/services" element={<ServicesPage />} />
                    <Route path="/products" element={<ProductsPage />} />
                    <Route path="/about" element={<AboutPage />} />
                    <Route path="/blog" element={<BlogPage />} />
                    <Route path="/blog/:slug" element={<BlogDetailPage />} />
                    <Route path="/contact" element={<ContactPage />} />
                    <Route path="/careers" element={<CareersPage />} />
                    <Route path="/terms" element={<TermsPage />} />
                    <Route path="/privacy" element={<PrivacyPage />} />
                    <Route path="/security" element={<SecurityPage />} />
                    <Route path="/cookies" element={<CookiesPage />} />
                    <Route path="/dashboard/*" element={<DashboardPage />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            }
          />
        </Routes>
        </AuthModalProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
